import { isDefined } from "@clipboard-health/util-ts";
import { type IconType } from "@src/appV2/redesign/components/Icon";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export interface TabConfig {
  iconType: IconType;
  label: string;
  value: string;
  onClick?: () => void;
}

interface FloatingTabsContextValue {
  tabs: TabConfig[];
  setTabs: (tabs: TabConfig[]) => void;
  showTabs: boolean;
  setShowTabs: (displayFloatingTabs: boolean) => void;
  activeTab: string | undefined;
  setActiveTab: (tab: string) => void;
}

const FloatingTabsContext = createContext<FloatingTabsContextValue | undefined>(undefined);

interface FloatingTabsProviderProps {
  children: ReactNode;
}

export function FloatingTabsProvider({ children }: FloatingTabsProviderProps) {
  const [tabs, setTabs] = useState<TabConfig[]>([]);
  const [showTabs, setShowTabs] = useState(false);
  const [activeTab, setActiveTab] = useState<string | undefined>();
  const location = useLocation();

  // Reset tabs when route changes
  useEffect(() => {
    setTabs([]);
    setActiveTab(undefined);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDefined(activeTab) || !tabs.map((element) => element.value).includes(activeTab)) {
      if (tabs.length > 0) {
        setActiveTab(tabs[0].value);
      } else {
        setActiveTab(undefined);
      }
    }
  }, [activeTab, tabs, setActiveTab]);

  const value = useMemo(
    () => ({ tabs, setTabs, showTabs, setShowTabs, activeTab, setActiveTab }),
    [tabs, setTabs, showTabs, setShowTabs, activeTab, setActiveTab]
  );

  return <FloatingTabsContext.Provider value={value}>{children}</FloatingTabsContext.Provider>;
}

export function useFloatingTabs(): FloatingTabsContextValue {
  const context = useContext(FloatingTabsContext);
  if (!context) {
    throw new Error("useFloatingTabs must be used within FloatingTabsProvider");
  }

  return context;
}
